import { HTMLAttributes, useCallback, useState, useEffect } from 'preact/compat'
import {
  DrawerBottomSheet,
  DrawerBottomHeader,
  DrawerBottomContent,
  DrawerBottomFooter,
  BarcodeList,
  BarcodeListItem,
  Button,
  ToastMessage,
} from '../../atoms'
import type { StorableBarcode } from '../../../store/BarcodeListStore'
import { ReadonlySignal, Signal } from '@preact/signals'

function RedText(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <div style="color: var(--scandit-color-secondary-red-100); font-weight: 400;cursor: pointer;" {...props}>
      {props.children}
    </div>
  )
}

export function ExpandableBarcodeList({
  totalQuantity,
  barcodes,
  onClearList,
  onExpandChange,
}: {
  totalQuantity: ReadonlySignal<number>
  barcodes: Signal<StorableBarcode[]>
  onClearList: () => void
  onExpandChange: (isExpanded: boolean) => void
}) {
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
  const [shouldShowToastMessage, setShowToastMessage] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (visible) return
    if (barcodes.peek().length > 0) {
      setVisible(true)
    }
  }, [barcodes.value.length, visible])

  const onLongPress = useCallback(async ({ data, id }: StorableBarcode) => {
    setHighlighted((oldSet) => {
      const newSet = new Set(oldSet)
      newSet.add(id)
      return newSet
    })
    try {
      if (data != null) {
        await navigator.clipboard.writeText(data)
        setShowToastMessage(true)
      }
    } catch (error: unknown) {
      console.warn('Cannot copy to clipboard', error)
    }
  }, [])

  const onReleasePress = useCallback(({ id }: StorableBarcode) => {
    setHighlighted((oldSet) => {
      const newSet = new Set(oldSet)
      newSet.delete(id)
      return newSet
    })
  }, [])

  return (
    <>
      <DrawerBottomSheet
        id="barcodesList"
        visible={visible}
        expanded={expanded}
        onHandleClick={() => {
          setExpanded((expanded) => {
            const toggled = !expanded
            onExpandChange(toggled)
            return toggled
          })
        }}
      >
        <DrawerBottomHeader>
          <b>{`${totalQuantity.value} items scanned`}</b>
          <RedText onClick={onClearList}>CLEAR LIST</RedText>
        </DrawerBottomHeader>
        <DrawerBottomContent>
          <BarcodeList>
            {barcodes.value.map((barcode) => (
              <BarcodeListItem
                barcode={barcode}
                highlighted={highlighted.has(barcode.id)}
                onLongPress={onLongPress}
                onReleasePress={onReleasePress}
              />
            ))}
          </BarcodeList>
        </DrawerBottomContent>
        <DrawerBottomFooter>
          <Button
            onClick={() => {
              setExpanded(false)
              onExpandChange(false)
            }}
          >
            RESUME SCANNING
          </Button>
        </DrawerBottomFooter>
      </DrawerBottomSheet>
      <ToastMessage timeout={3000} show={shouldShowToastMessage} onFinishedTimeout={() => setShowToastMessage(false)}>
        Copied to clipboard
      </ToastMessage>
    </>
  )
}
